import React from 'react';
import { RulerDimensionLine } from 'lucide-react';
import Hyperlink from '@ajat/design-system/Hyperlink';
import Input from '@ajat/design-system/Input';
import maxLengthFromRegex, { sanitizeForRegex, inputModeFromRegex } from './attributeTextLimits';
import { PRODUCT_FOCUS_REVEAL } from './focusReveal';
import {
  HeadingSmall,
  ColorSelector,
  LabelSelector,
  LargeLabelSelector,
} from '@ajat/design-system/Configurator';

/**
 * Format an attribute value's additional_price as a "+NN,-" tag the way the
 * Navnetryk add-on does. Returns null when there's no charge so the heading
 * skips the price slot entirely.
 */
const formatAdditionalPrice = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `+${Math.round(n)},-`;
};

/**
 * Shared attribute selector used by every brand PDP.
 *
 * Renders three blocks against the unified product shape coming from the BE
 * (`product.attributes`):
 *   - Canonical color block (only the attribute with slug === 'color')
 *   - Canonical size block  (only the attribute with slug === 'size')
 *   - Extra attributes block (everything else, rendered by `type`)
 *
 * State is owned by the parent. Selected values are tracked as:
 *   - `selectedColor`       — index into the `colors` array
 *   - `selectedSize`        — the size value (string)
 *   - `selectedAttributes`  — `{ [slug]: valueId }` for every extra attribute
 *
 * Pinned values (locked by a school-shop variation) are non-interactive.
 */
const AttributeSelector = ({
  colors = [],
  sizes = [],
  attributes = [],
  selectedColor,
  selectedSize,
  selectedAttributes = {},
  onColorChange,
  onSizeChange,
  onAttributeChange,
  /** Called with the attribute slug when a textfield gains focus (3D pages
   *  move the camera to the print the customer is about to type). */
  onAttributeFocus,
  /** Mobile focus mode (see DynamicAttributeControls): with `isMobile`, a
   *  textfield becomes a `mf__trigger` opening the page's MobileFocusModal. */
  openFocusMode,
  isMobile = false,
  onAttributeBlur,
  typingMode = false,
  onTypingChange,
  pinnedColorIdxs,
  sizeError,
  labels = {},
  /** When true, required textfields still empty show labels.requiredError
   *  inline — the PDP flips this on after a blocked add-to-cart attempt. */
  showRequiredErrors = false,
  /** Slugs of revealed-by-rule select/dropdown controls that must be answered
   *  (see findUnansweredRevealedSlugs) — they error like required fields when
   *  `showRequiredErrors` flips on. */
  requiredEmptySlugs = new Set(),
  onSizeGuideOpen,
  showSizeGuide = true,
  // Tree-aware constraints. Each is optional; absence ⇒ no constraint.
  disabledColorIdxs,                 // Set<number> — indexes into `colors`
  disabledSizes,                     // Set<string> — size string values
  disabledValueIdsByAttribute = {},  // { [slug]: Set<valueId> } — for extras
  // Sold-out options are HIDDEN entirely (skipped in render — never
  // pre-filter the arrays: color selection is index-based).
  soldOutColorIdxs,                  // Set<number> — indexes into `colors`
  soldOutSizes,                      // Set<string> — size string values
  soldOutValueIdsByAttribute = {},   // { [slug]: Set<valueId> } — for extras
  // Control-visibility: slugs whose control is currently hidden (treated as
  // disabled). Hidden controls are not rendered here at all.
  hiddenControlSlugs = new Set(),
  // When true, only color-typed extra attributes render here; the rest are
  // expected to render elsewhere (e.g. a brand add-ons section). Default keeps
  // every extra attribute inline.
  excludeNonColorExtras = false,
}) => {
  const lbl = {
    color: 'Color',
    size: 'Size',
    sizeGuide: 'Size guide',
    ...labels,
  };
  const hiddenSet = hiddenControlSlugs instanceof Set ? hiddenControlSlugs : new Set(hiddenControlSlugs || []);
  const requiredEmptySet = requiredEmptySlugs instanceof Set ? requiredEmptySlugs : new Set(requiredEmptySlugs || []);
  const showColor = !hiddenSet.has('color');
  const showSize  = !hiddenSet.has('size');
  const extraAttrs = attributes.filter(
    (a) => a.slug !== 'color' && a.slug !== 'size'
      && !hiddenSet.has(a.slug)
      && (!excludeNonColorExtras || a.type === 'color'),
  );
  const pinnedSet = pinnedColorIdxs instanceof Set ? pinnedColorIdxs : new Set();
  const disabledColorSet = disabledColorIdxs instanceof Set ? disabledColorIdxs : new Set();
  const disabledSizeSet  = disabledSizes  instanceof Set ? disabledSizes  : new Set();
  const soldOutColorSet = soldOutColorIdxs instanceof Set ? soldOutColorIdxs : new Set();
  const soldOutSizeSet  = soldOutSizes  instanceof Set ? soldOutSizes  : new Set();
  const anyColorVisible = colors.some((c, i) => !soldOutColorSet.has(i));
  const anySizeVisible  = sizes.some((s) => !soldOutSizeSet.has(s));

  // Inline disabled styling — keeps the change self-contained without
  // touching brand stylesheets.
  const disabledStyle = { opacity: 0.35, cursor: 'not-allowed' };

  // User-initiated changes also report the attribute as "in focus" (3D pages
  // move the camera); the auto-selection effects above deliberately don't.
  const userChange = (slug, value) => { onAttributeFocus?.(slug); onAttributeChange?.(slug, value); };
  return (
    <>
      {showColor && colors.length > 0 && anyColorVisible && (
        <div className="pp__section">
          <HeadingSmall
            title={lbl.color}
            selection={colors[selectedColor]?.name?.toUpperCase()}
          />
          <div className="pp__color-row">
            {colors.map((c, i) => {
              if (soldOutColorSet.has(i)) return null;
              const isPinned   = pinnedSet.has(i);
              const isDisabled = disabledColorSet.has(i);
              return (
                <div key={i} style={isDisabled ? disabledStyle : undefined}>
                  <ColorSelector
                    color={c.color}
                    name={c.name}
                    selected={i === selectedColor}
                    onClick={(isPinned || isDisabled) ? undefined : () => onColorChange?.(i)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showSize && sizes.length > 0 && anySizeVisible && (
        <div className={`pp__section pp__size-section ${sizeError ? 'pp__size-section--error' : ''}`}>
          <HeadingSmall title={lbl.size} selection={selectedSize} />
          <div className="pp__size-row">
            {sizes.map((s) => {
              if (soldOutSizeSet.has(s)) return null;
              const isDisabled = disabledSizeSet.has(s);
              return (
                <div key={s} style={isDisabled ? disabledStyle : undefined}>
                  <LabelSelector
                    selected={s === selectedSize}
                    onClick={isDisabled ? undefined : () => onSizeChange?.(s)}
                  >
                    {s}
                  </LabelSelector>
                </div>
              );
            })}
          </div>
          {showSizeGuide && (
            <div className="pp__size-guide">
              <Hyperlink
                variant="regular"
                iconLeft={<RulerDimensionLine size={20} />}
                showIconLeft
                href="#"
                onClick={(e) => { e.preventDefault(); onSizeGuideOpen?.(); }}
              >
                {lbl.sizeGuide}
              </Hyperlink>
            </div>
          )}
          {sizeError && (
            <div className="pp__size-error" role="alert">
              <span className="pp__size-error-text typo-body-sm">{sizeError}</span>
            </div>
          )}
        </div>
      )}

      {extraAttrs.map((attr) => {
        const selectedId = selectedAttributes[attr.slug];
        const pinnedIds  = attr.pinnedValueIds ?? [];
        const disabledIds = disabledValueIdsByAttribute[attr.slug] ?? null;

        if (attr.type === 'textfield') {
          // Mirrors CLS-DK's static "Navnetryk" add-on: HeadingSmall with the
          // attribute's name + price tag, then a DS Input. Empty input ⇒ no
          // price contribution. Live-validates against the attribute's regex
          // (the BE rejects failing input at add-to-cart with a 422) so the
          // customer sees the problem on the field itself.
          const anchor = attr.values?.[0];
          const priceTag = formatAdditionalPrice(anchor?.additional_price);
          const currentValue = typeof selectedId === 'string' ? selectedId : '';
          const isRequired = !!attr.is_required;
          let patternError = '';
          const trimmedValue = currentValue.trim();
          if (trimmedValue && attr.regex) {
            try {
              if (!(new RegExp(attr.regex, 'u')).test(trimmedValue)) {
                patternError = labels.textfieldError || 'Teksten er for lang eller indeholder ugyldige tegn';
              }
            } catch { /* regex not valid in JS — the server stays the judge */ }
          }
          const requiredError = (isRequired && showRequiredErrors && !trimmedValue)
            ? (labels.requiredError || 'Fyll i detta fält')
            : '';
          const useFocusTrigger = isMobile && typeof openFocusMode === 'function';
          /* The focus modal owns mobile editing when it is in play; where it is not
           * (3D pages), the field pins itself above the keyboard instead. */
          const input = (
            <Input
              type="text"
              showIcon={false}
              label={attr.name}
              placeholder={attr.name}
              value={currentValue}
              pattern={attr.regex || undefined}
              maxLength={maxLengthFromRegex(attr.regex) || undefined}
              inputMode={inputModeFromRegex(attr.regex) || undefined}
              helperText={attr.hint || ''}
              error={patternError || requiredError}
              // Characters the attribute's class forbids never enter the field (Nummertryk: digits only).
              onChange={(e) => userChange(attr.slug, sanitizeForRegex(e.target.value, attr.regex))}
              onFocus={() => onAttributeFocus?.(attr.slug)}
              readOnly={useFocusTrigger}
              tabIndex={useFocusTrigger ? -1 : undefined}
              onBlur={() => onAttributeBlur?.(attr.slug)}
              mobileFocus={!useFocusTrigger && !!typingMode}
              mobileFocusPlacement="bottom"
              onMobileFocusChange={!useFocusTrigger && typingMode
                ? (active) => onTypingChange?.(active, attr.slug)
                : undefined}
            />
          );
          return (
            <div key={attr.slug} className="pp__section" data-required-empty={requiredError ? attr.slug : undefined}>
              <HeadingSmall
                title={isRequired ? `${attr.name} *` : attr.name}
                showSelection={false}
                extraInfo={attr.subtitle || undefined}
                price={priceTag || undefined}
              />
              {useFocusTrigger ? (
                <div
                  id={`mf-trigger-${attr.slug}`}
                  className="mf__trigger"
                  onClick={(e) => { e.preventDefault(); onAttributeFocus?.(attr.slug); openFocusMode(attr.slug); }}
                >
                  {input}
                </div>
              ) : input}
            </div>
          );
        }

        if (attr.type === 'dropdown') {
          // Mirrors CLS-DK's static "Flag" add-on: HeadingSmall + DS Input
          // type=searchable with options. When the attribute has the Option
          // thumbnails flag on, each value's image_url renders as the icon
          // in the dropdown and (when selected) inside the input.
          const selectedVal = attr.values?.find((v) => v.id === selectedId);
          const priceTag = formatAdditionalPrice(selectedVal?.additional_price);
          const useThumbs = !!attr.option_thumbnails;
          const thumb = (url) => url
            ? <img src={url} alt="" className="pp__dropdown-thumb" />
            : undefined;
          const selectedIcon = useThumbs ? thumb(selectedVal?.image_url) : undefined;
          const options = (attr.values || []).map((v) => ({
            value: String(v.id),
            label: v.value,
            icon: useThumbs ? thumb(v.image_url) : undefined,
          }));
          // Revealed-by-rule detail dropdown (letter, country) left unanswered:
          // errors like a required field once add-to-cart was attempted.
          const revealedError = (showRequiredErrors && requiredEmptySet.has(attr.slug) && selectedId == null)
            ? (lbl.requiredError || 'Please fill in this field') : '';
          return (
            <div key={attr.slug} className="pp__section" data-required-empty={revealedError ? attr.slug : undefined}>
              <HeadingSmall
                title={attr.name}
                showSelection={false}
                extraInfo={attr.subtitle || undefined}
                price={priceTag || undefined}
              />
              <Input
                type="searchable"
                mobileFocus
                mobileFocusReveal={PRODUCT_FOCUS_REVEAL}
                label={attr.name}
                placeholder={attr.name}
                showIcon={!!selectedIcon}
                icon={selectedIcon}
                showOptionIcons={useThumbs}
                options={options}
                value={selectedVal?.value || ''}
                helperText={attr.hint || ''}
                error={revealedError || undefined}
                onSelect={(opt) => userChange(attr.slug, opt ? Number(opt.value) : null)}
                onFocus={() => onAttributeFocus?.(attr.slug)}
              />
            </div>
          );
        }

        const selectedVal = attr.values.find((v) => v.id === selectedId);
        const headingPrice = formatAdditionalPrice(selectedVal?.additional_price);

        if (attr.type === 'color') {
          return (
            <div key={attr.slug} className="pp__section">
              <HeadingSmall
                title={attr.name}
                selection={selectedVal?.value?.toUpperCase()}
                extraInfo={attr.subtitle || undefined}
                price={headingPrice || undefined}
              />
              <div className="pp__color-row">
                {attr.values.map((val) => {
                  const soldOutIds = soldOutValueIdsByAttribute[attr.slug];
                  if (soldOutIds instanceof Set && soldOutIds.has(val.id)) return null;
                  const isPinnedVal = pinnedIds.includes(val.id);
                  const isDisabled  = disabledIds instanceof Set && disabledIds.has(val.id);
                  return (
                    <div key={val.id} style={isDisabled ? disabledStyle : undefined}>
                      <ColorSelector
                        color={val.rawValue ?? val.value}
                        name={val.value}
                        selected={selectedId === val.id}
                        onClick={(isPinnedVal || isDisabled)
                          ? undefined
                          : () => userChange(attr.slug, val.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        const selectRevealedError = (showRequiredErrors && requiredEmptySet.has(attr.slug) && selectedId == null)
          ? (lbl.requiredError || 'Please fill in this field') : '';
        return (
          <div key={attr.slug} className="pp__section" data-required-empty={selectRevealedError ? attr.slug : undefined}>
            <HeadingSmall
              title={attr.name}
              selection={selectedVal?.value}
              extraInfo={attr.subtitle || undefined}
              price={headingPrice || undefined}
            />
            <div className="pp__select-attr-row">
              {attr.values.map((val) => {
                const soldOutIds = soldOutValueIdsByAttribute[attr.slug];
                if (soldOutIds instanceof Set && soldOutIds.has(val.id)) return null;
                const isPinnedVal = pinnedIds.includes(val.id);
                const isDisabled  = disabledIds instanceof Set && disabledIds.has(val.id);
                const valuePrice = formatAdditionalPrice(val.additional_price);
                return (
                  <div key={val.id} style={isDisabled ? disabledStyle : undefined}>
                    <LargeLabelSelector
                      selected={selectedId === val.id}
                      onClick={(isPinnedVal || isDisabled)
                        ? undefined
                        : () => userChange(attr.slug, val.id)}
                    >
                      {valuePrice ? `${val.value} (${valuePrice})` : val.value}
                    </LargeLabelSelector>
                  </div>
                );
              })}
            </div>
            {selectRevealedError && (
              <div className="typo-body-sm" style={{ color: 'var(--color-ui-error)', marginTop: '6px' }}>
                {selectRevealedError}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default AttributeSelector;
